import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Work" },
  { to: "/courses", label: "Courses" },
  { to: "/blog", label: "Blogs" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#121212]/80 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.35)]"
            : "bg-transparent"
        }`}
      >
        <div className="relative mx-auto h-24 max-w-[1600px]">

          {/* Logo */}

          <Link
            to="/"
            className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center gap-3 group lg:left-8"
          >
            <img
              src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2025/08/White-and-Blue-Minimalist-Technology-Letter-D-Logo-7-e1770916697848.png"
              alt="Dorado9"
              className="
  h-14
  w-[125px]
  lg:h-[65px]
  lg:w-[150px]
  object-contain
  transition-all
  duration-500
  ease-out
  group-hover:scale-[1.04]
  group-hover:-translate-y-[2px]
  group-hover:drop-shadow-[0_0_14px_rgba(17,245,186,0.35)]
"
            />

            
          </Link>

          {/* Desktop Navigation */}

          <nav className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] justify-between">

            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative uppercase text-[13px] tracking-[0.24em] font-medium transition-colors duration-300 ${
                    isActive
                      ? "text-brand-mint"
                      : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}

                    <motion.span
                      layoutId="navLine"
                      className={`absolute -bottom-3 left-0 h-[2px] rounded-full bg-brand-mint ${
                        isActive ? "w-full" : "w-0"
                      }`}
                      transition={{
                        type: "spring",
                        stiffness: 350,
                        damping: 30,
                      }}
                    />
                  </>
                )}
              </NavLink>
            ))}

          </nav>

          {/* Premium CTA */}

          <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2">

            <Link
              to="/contact"
              className="group flex items-center gap-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:border-brand-mint/40 hover:bg-brand-mint hover:text-black"
            >
              Let's Talk

              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-mint text-black transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={16} />
              </span>
            </Link>

          </div>

          {/* Mobile Button */}

          <button
            onClick={() => setOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={open}
            aria-controls="mobile-navigation"
            className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full border border-zinc-700 p-2 text-white transition-colors hover:border-brand-mint lg:hidden"
          >
            <Menu size={22} />
          </button>

        </div>
      </header>

      <AnimatePresence>
                {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-[#121212]/95 backdrop-blur-2xl"
          >
            {/* Top Bar */}
            <div className="flex items-center justify-between px-6 pb-5 pt-[max(1.25rem,env(safe-area-inset-top))]">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3"
              >
                <img
                  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2025/08/White-and-Blue-Minimalist-Technology-Letter-D-Logo-7-e1770916697848.png"
                  alt="Dorado9"
                  className="h-10 w-24 object-contain brightness-0 invert"
                />
              </Link>

              <button
                onClick={() => setOpen(false)}
                aria-label="Close navigation menu"
                className="rounded-full border border-white/10 p-2 text-white transition-colors hover:border-brand-mint"
              >
                <X size={22} />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav id="mobile-navigation" className="flex min-h-[calc(100dvh-5rem)] flex-col justify-start px-6 pb-8 pt-10 sm:px-10">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.12 + i * 0.06,
                    duration: 0.55,
                  }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block py-2 font-display text-[clamp(2.25rem,11vw,3rem)] leading-[1.05] transition-colors ${
                      location.pathname === l.to
                        ? "text-brand-mint"
                        : "text-white hover:text-brand-mint"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
